import React from 'react';
import { FadeIn } from '../components/FadeIn';
import { SectionLabel } from '../components/SectionLabel';
import { XIcon, CheckIcon } from 'lucide-react';
export function ComparisonSection() {
  return <section className="py-14 md:py-20 px-6 bg-midnight-light relative" data-id="element-93">
      <div className="max-w-5xl mx-auto" data-id="element-94">
        <FadeIn className="text-center mb-16" data-id="element-95">
          <SectionLabel text="The Real Reason Nothing Worked" data-id="element-96" />
          <h2 className="font-playfair text-3xl md:text-5xl font-bold text-warmwhite mb-6 leading-tight" data-id="element-97">
            It's Not Your Fault.
            <br data-id="element-98" />
            The Methods Were Wrong — Not You.
          </h2>
          <p className="font-inter text-lg text-softgray max-w-[680px] mx-auto" data-id="element-99">
            Traditional methods were designed for calm moments — not for the
            biological firestorm happening inside your body right now. Here's
            the difference:
          </p>
        </FadeIn>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-16" data-id="element-100">
          {/* The Old Way Card */}
          <FadeIn delay={0.2} direction="right" data-id="element-101">
            <div className="bg-red-950/20 border border-red-900/30 rounded-2xl p-8 h-full" data-id="element-102">
              <div className="flex items-center mb-8" data-id="element-103">
                <div className="w-10 h-10 rounded-full bg-red-900/30 flex items-center justify-center mr-4" data-id="element-104">
                  <XIcon className="w-5 h-5 text-red-400" data-id="element-105" />
                </div>
                <h3 className="font-playfair text-2xl font-semibold text-red-100" data-id="element-106">
                  The Old Way
                </h3>
              </div>
              <ul className="space-y-6 font-inter text-red-200/80" data-id="element-107">
                <li className="flex items-start" data-id="element-108">
                  <span className="font-bold text-red-300 mr-2 min-w-[140px]" data-id="element-109">
                    Guided Meditation:
                  </span>
                  <span data-id="element-110">Requires 10-20 minutes of silence you don't have</span>
                </li>
                <li className="flex items-start" data-id="element-111">
                  <span className="font-bold text-red-300 mr-2 min-w-[140px]" data-id="element-112">
                    Counting to 10:
                  </span>
                  <span data-id="element-113">Does nothing to your cortisol levels</span>
                </li>
                <li className="flex items-start" data-id="element-114">
                  <span className="font-bold text-red-300 mr-2 min-w-[140px]" data-id="element-115">
                    Journaling:
                  </span>
                  <span data-id="element-116">Great — after the damage is done</span>
                </li>
                <li className="flex items-start" data-id="element-117">
                  <span className="font-bold text-red-300 mr-2 min-w-[140px]" data-id="element-118">
                    Deep Breathing:
                  </span>
                  <span data-id="element-119">Too slow. Anger moves faster.</span>
                </li>
                <li className="flex items-start" data-id="element-120">
                  <span className="font-bold text-red-300 mr-2 min-w-[140px]" data-id="element-121">
                    Therapy:
                  </span>
                  <span data-id="element-122">Helps long term. Useless in the moment.</span>
                </li>
              </ul>
            </div>
          </FadeIn>

          {/* The New Way Card */}
          <FadeIn delay={0.4} direction="left" data-id="element-123">
            <div className="bg-teal-950/20 border border-teal-800/40 rounded-2xl p-8 h-full relative overflow-hidden" data-id="element-124">
              <div className="absolute top-0 right-0 w-64 h-64 bg-lavender/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-125" />
              <div className="relative z-10" data-id="element-126">
                <div className="flex items-center mb-8" data-id="element-127">
                  <div className="w-10 h-10 rounded-full bg-teal-900/40 flex items-center justify-center mr-4" data-id="element-128">
                    <CheckIcon className="w-5 h-5 text-teal-400" data-id="element-129" />
                  </div>
                  <h3 className="font-playfair text-2xl font-semibold text-teal-50" data-id="element-130">
                    The Anger Circuit Breaker Way
                  </h3>
                </div>
                <ul className="space-y-6 font-inter text-teal-100/90" data-id="element-131">
                  <li className="flex items-start" data-id="element-132">
                    <CheckIcon className="w-5 h-5 text-teal-500 mr-3 shrink-0 mt-0.5" data-id="element-133" />
                    <span data-id="element-134">Works in 55 seconds flat</span>
                  </li>
                  <li className="flex items-start" data-id="element-135">
                    <CheckIcon className="w-5 h-5 text-teal-500 mr-3 shrink-0 mt-0.5" data-id="element-136" />
                    <span data-id="element-137">
                      Targets your nervous system directly — not your thoughts
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-138">
                    <CheckIcon className="w-5 h-5 text-teal-500 mr-3 shrink-0 mt-0.5" data-id="element-139" />
                    <span data-id="element-140">Works mid-argument, mid-drive, mid-panic</span>
                  </li>
                  <li className="flex items-start" data-id="element-141">
                    <CheckIcon className="w-5 h-5 text-teal-500 mr-3 shrink-0 mt-0.5" data-id="element-142" />
                    <span data-id="element-143">
                      No app needed. No silence needed. No willpower needed.
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-144">
                    <CheckIcon className="w-5 h-5 text-teal-500 mr-3 shrink-0 mt-0.5" data-id="element-145" />
                    <span data-id="element-146">
                      Based on vagus nerve activation + hyper-oxygenation
                      sequencing
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </FadeIn>
        </div>

        <FadeIn delay={0.6} data-id="element-147">
          <p className="font-playfair text-xl md:text-2xl text-center text-warmwhite italic max-w-4xl mx-auto leading-relaxed" data-id="element-148">
            "When your body is in fight-or-flight, your rational mind is
            offline. No amount of 'thinking positive' will stop a cortisol
            spike. But one specific breathing sequence will."
          </p>
        </FadeIn>
      </div>
    </section>;
}